import { useMemo, useState } from "react";
import { Calculator as CalcIcon, ArrowRight } from "lucide-react";
import { SERVICES } from "../data/content";
import { Reveal, SectionHeading } from "./Reveal";

export default function Calculator({ onGetQuote }) {
  const [selected, setSelected] = useState(["it-support"]);
  const [hours, setHours] = useState(2);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const total = useMemo(
    () => SERVICES.filter((s) => selected.includes(s.id)).reduce((acc, s) => acc + s.rateVal, 0) * hours,
    [selected, hours]
  );

  const summary = useMemo(() => {
    const names = SERVICES.filter((s) => selected.includes(s.id))
      .map((s) => s.title)
      .join(", ");
    return `Guide estimate from the website calculator: ${names} · approx ${hours} hour${hours > 1 ? "s" : ""} per service · from £${total}.`;
  }, [selected, hours, total]);

  return (
    <section id="pricing" className="relative border-y border-white/5 bg-[#0A1128] py-20 md:py-28" data-testid="pricing-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <SectionHeading
          overline="Transparent pricing"
          title="Know the cost before you commit"
          copy="No call-out surprises and no vague day rates. Pick what you need below for an instant guide estimate — every job is then confirmed with a clear, fixed quote before work starts."
        />

        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-cyan-500/20 bg-[#0D152D] p-6 shadow-[0_0_40px_rgba(0,102,255,0.12)] md:p-8" data-testid="calculator-card">
            <div className="flex items-center gap-2.5">
              <CalcIcon size={18} className="text-cyan-400" />
              <h3 className="font-display text-lg font-semibold text-slate-50">Instant guide estimate</h3>
            </div>

            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">1 · Select services</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {SERVICES.map((s) => {
                const active = selected.includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`calc-service-${s.id}`}
                    onClick={() => toggle(s.id)}
                    className="rounded-full border px-3.5 py-2 text-xs font-medium transition-all duration-200"
                    style={{
                      borderColor: active ? s.color : "rgba(255,255,255,0.1)",
                      background: active ? `${s.color}1a` : "transparent",
                      color: active ? s.color : "#94A3B8",
                    }}
                  >
                    {s.title}
                  </button>
                );
              })}
            </div>

            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              2 · Hours needed per service — <span className="text-cyan-300">{hours}h</span>
            </p>
            <input
              data-testid="calc-hours-slider"
              type="range"
              min="1"
              max="10"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="mt-3 w-full"
              aria-label="Hours needed"
            />
            <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-600">
              <span>1h</span>
              <span>10h</span>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Guide estimate</p>
                <p className="font-display text-3xl font-bold text-cyan-300" data-testid="calc-total">
                  from £{total}
                </p>
              </div>
              <button
                data-testid="calc-quote-button"
                onClick={() => total > 0 && onGetQuote(summary)}
                disabled={total === 0}
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-5 py-3 text-sm font-semibold text-[#04101F] transition-transform duration-200 hover:scale-[1.03] disabled:opacity-40"
              >
                Request this estimate
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
              Guide only — final price is always confirmed in writing before any work begins.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
